import { useState, useEffect, useMemo, useCallback } from 'react';
import { Conversation } from './lib/data';
import { ConversationTable } from './components/ConversationTable';
import { CurrentOperationsSummary, ApiSummaryResponse } from './components/CurrentOperationsSummary';
import { ConversationDetailSheet } from './components/ConversationDetailSheet';
import { SentimentDistributionPlot } from './components/SentimentDistributionPlot';
import { EmotionDistributionOverTime } from './components/EmotionDistributionOverTime';
import { SentimentTrendOverTime } from './components/SentimentTrendOverTime';
import { EmotionTrendOverTime } from './components/EmotionTrendOverTime';
import { TrendingTopics } from './components/TrendingTopics';
import { AgentPerformanceSummariser, AgentPerformance } from './components/AgentPerformanceSummariser';
import { ClientSummary } from './components/ClientSummary';
import { AIChatAssistant } from './components/AIChatAssistant';
import { DashboardFilters, FilterState } from './components/DashboardFilters';
import { MessageCircle, CheckCircle, AlertTriangle, ThumbsDown, AlertCircle } from 'lucide-react';
import { isWithinInterval } from 'date-fns';
import { Team } from './components/ChannelNavBar';
import { ClientPerformanceData } from './lib/types';
import { StatsCard } from './components/StatsCard';
import { FilterType } from './components/DateFilter';

const API_BASE = import.meta.env.VITE_AWS_API as string;

// New: Utility function to convert a team name into a URL-friendly slug
// E.g., "Owner Relation API Channel" -> "owner-relation-api-channel"
const toUrlSlug = (name: string): string => {
  if (!name) return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/ & /g, '-and-') // Replace ' & ' with '-and-'
    .replace(/[\s\W-]+/g, '-') // Replace spaces and non-word chars with a single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

interface Client {
  id: string;
  name: string;
  age: number;
  gender: string;
  property: string;
  bigFivePersonality: {
    openness: number;
    conscientiousness: number;
    extraversion: number;
    agreeableness: number;
    neuroticism: number;
  };
}

export interface Agent {
  id: string;
  name: string;
  role: string;
}

export type DateRange = {
  start: Date;
  end: Date;
} | null;

const initialStats = {
  total: 0,
  inProgress: 0,
  solved: 0,
  negative: 0,
  urgent: 0,
  agentCount: 0,
  avgResponseTime: 0
};

export default function DashboardPage({ currentTeam }: { currentTeam: Team }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiDown, setApiDown] = useState(false);

  const [summaryApiData, setSummaryApiData] = useState<ApiSummaryResponse | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryFilter, setSummaryFilter] = useState<{
    type: FilterType;
    date: Date | null;
  }>({ type: 'daily', date: null });

  const summaryDateRange = useMemo(() => {
    const { type, date } = summaryFilter;
    if (!date) return null;

    const selectedDate = new Date(date);
    let start: Date;
    let end: Date;

    switch (type) {
      case 'weekly':
        start = new Date(selectedDate);
        start.setDate(selectedDate.getDate() - selectedDate.getDay());
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'monthly':
        start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        break;
      case 'daily':
      default:
        start = selectedDate;
        end = selectedDate;
        break;
    }
    
    const toISOString = (d: Date) => d.toISOString().split('T')[0];
    return { start: toISOString(start), end: toISOString(end) };
  }, [summaryFilter]);

  const [trendSelectedRange, setTrendSelectedRange] = useState<'today' | 'week' | 'month' | 'custom'>('week');
  const [trendCustomDateRange, setTrendCustomDateRange] = useState<{ from?: Date; to?: Date }>({ from: undefined, to: undefined });

  type DateRangeType = 'today' | 'week' | 'month' | 'custom';
  const [selectedRange, setSelectedRange] = useState<DateRangeType>('week');
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({ from: undefined, to: undefined });

  const [agentPerformanceDateRange, setAgentPerformanceDateRange] = useState<DateRange>(null);
  const [agentPerformance, setAgentPerformance] = useState<AgentPerformance[]>([]);

  const [clientPerformanceDateRange, setClientPerformanceDateRange] = useState<DateRange>(null);
  const [clientPerformanceData, setClientPerformanceData] = useState<ClientPerformanceData[]>([]);

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: [],
    sentiment: [],
    emotions: [],
    topics: [],
    agent: [],
    dateRange: undefined
  });

  const [stats, setStats] = useState(initialStats);

  // Modified: API base path is now dynamically generated from the currentTeam prop
  const dynamicApiBase = useMemo(() => {
    const teamPath = toUrlSlug(currentTeam);
    return `${API_BASE}/${teamPath}`;
  }, [currentTeam]);

  // --- All useEffect hooks will now re-run automatically when `dynamicApiBase` changes ---

  useEffect(() => {
    const fetchSummary = async () => {
      if (!summaryDateRange) {
        setSummaryApiData(null);
        return;
      }

      setIsSummaryLoading(true);
      setSummaryError(null);
      
      try {
        const url = `${dynamicApiBase}/current-operation-summary?start_date=${summaryDateRange.start}&end_date=${summaryDateRange.end}`;
        console.log("🔄 Fetching summary from (Dashboard):", url);
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data: ApiSummaryResponse = await response.json();
        console.log("✅ API Summary Response (Dashboard):", data);
        setSummaryApiData(data);
      } catch (err) {
        console.error("❌ Error fetching API summary (Dashboard):", err);
        setSummaryError(err instanceof Error ? err.message : 'Failed to fetch summary');
        setSummaryApiData(null);
      } finally {
        setIsSummaryLoading(false);
      }
    };

    fetchSummary();
  }, [summaryDateRange, dynamicApiBase]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setApiDown(false);
    setConversations([]);

    fetch(`${dynamicApiBase}/conversations`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
          const normalized: Conversation[] = data.map((c: any) => ({
            ...c,
            id: String(c.id),
            clientId: String(c.clientId),
            agentIds: (c.agentIds || []).map((a: any) => String(a)),
            lastUpdated: c.lastUpdated ? new Date(c.lastUpdated) : new Date(),
            startedAt: c.startedAt ? new Date(c.startedAt) : new Date(),
            summary: c.summary ?? "",
            status: c.status ?? "unknown",
            sentiment: c.sentiment ?? "neutral",
            emotions: Array.isArray(c.emotions) ? c.emotions : [],
            topics: Array.isArray(c.topics) ? c.topics : [],
            messages: (c.messages || []).map((m: any) => ({
              ...m,
              id: String(m.id),
              senderId: String(m.senderId),
              timestamp: new Date(m.timestamp),
              sentiment: m.sentiment ?? 0,
              emotionScores: m.emotionScores ?? {}
            }))
          }));
          setConversations(normalized);
        } else {
          console.warn("⚠️ Conversations API did not return an array:", data);
          setConversations([]);
        }
      })
      .catch(err => {
        console.error("❌ Error fetching conversations:", err);
        setError(`Failed to fetch conversations: ${err.message}`);
        setApiDown(true);
        setConversations([]);
      })
      .finally(() => setLoading(false));
  }, [dynamicApiBase]);

  useEffect(() => {
    setStats(initialStats);

    fetch(`${dynamicApiBase}/kpis`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data) {
          setStats({
            total: data.total ?? 0,
            inProgress: data.inProgress ?? 0,
            solved: data.solved ?? 0,
            negative: data.negative ?? 0,
            urgent: data.urgent ?? 0,
            agentCount: data.agentCount ?? 0,
            avgResponseTime: data.avgResponseTime ?? 0,
          });
        }
      })
      .catch(err => {
        console.error("❌ Error fetching stats cards:", err);
        setError(`Failed to fetch stats cards: ${err.message}`);
        setApiDown(true);
      });
  }, [dynamicApiBase]);

  useEffect(() => {
    setClients([]);
    setAgents([]);

    fetch(`${dynamicApiBase}/clients`)
      .then((res) => res.json())
      .then((data) => {
        const clientList = Array.isArray(data) ? data : data.clients || data.data || [];
        setClients(clientList);
      })
      .catch((err) => console.error("❌ Error fetching clients:", err));
      
    fetch(`${dynamicApiBase}/agents`)
      .then((res) => res.json())
      .then((data) => {
        const agentList = Array.isArray(data) ? data : data.agents || [];
        setAgents(agentList);
      })
      .catch((err) => console.error("❌ Error fetching agents:", err));
  }, [dynamicApiBase]);

  useEffect(() => {
    if (!agentPerformanceDateRange) {
      setAgentPerformance([]);
      return;
    }

    const { start, end } = agentPerformanceDateRange;
    const startDateString = start.toISOString().split("T")[0];
    const endDateString = end.toISOString().split("T")[0];
    const url = `${dynamicApiBase}/agents-insight?start_date=${startDateString}&end_date=${endDateString}`;

    console.log("📡 Fetching Agent Performance:", url);
    setAgentPerformance([]);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          console.log("✅ Agents Performance API response:", data);
          setAgentPerformance(data);
        } else {
          console.warn("⚠️ Agent Performance API did not return an array:", data);
          setAgentPerformance([]);
        }
      })
      .catch(err => {
        console.error("❌ Error fetching agent performance:", err);
        setError(`Failed to fetch agent performance: ${err.message}`);
        setApiDown(true);
        setAgentPerformance([]);
      });
  }, [agentPerformanceDateRange, dynamicApiBase]);

  useEffect(() => {
    if (!clientPerformanceDateRange) {
      setClientPerformanceData([]);
      return;
    }

    const { start, end } = clientPerformanceDateRange;
    const startDateString = start.toISOString().split("T")[0];
    const endDateString = end.toISOString().split("T")[0];
    const url = `${dynamicApiBase}/clients-insight?start_date=${startDateString}&end_date=${endDateString}`;

    console.log("📡 Fetching Client Performance:", url);
    setClientPerformanceData([]);

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: ClientPerformanceData[]) => {
        if(Array.isArray(data)) {
          console.log("✅ Client Performance API response:", data);
          setClientPerformanceData(data);
        } else {
          console.warn("⚠️ Client Performance API did not return an array:", data);
          setClientPerformanceData([]);
        }
      })
      .catch(err => {
        console.error("❌ Error fetching client performance:", err);
        setError(`Failed to fetch client performance: ${err.message}`);
        setApiDown(true);
        setClientPerformanceData([]);
      });
  }, [clientPerformanceDateRange, dynamicApiBase]);
    
  const filteredConversations = useMemo(() => {
    return conversations.filter(conv => {
      const searchLower = filters.search.toLowerCase();
      const client = clients.find(t => t.id === conv.clientId);
      const inSearch = filters.search === '' ||
        conv.id.toLowerCase().includes(searchLower) ||
        conv.summary.toLowerCase().includes(searchLower) ||
        (client && client.name.toLowerCase().includes(searchLower));

      const inDateRange = !filters.dateRange?.from ||
        isWithinInterval(conv.startedAt, {
            start: filters.dateRange.from,
            end: filters.dateRange.to || new Date()
        });

      const inStatus = filters.status.length === 0 || filters.status.includes(conv.status);
      const inSentiment = filters.sentiment.length === 0 || filters.sentiment.includes(conv.sentiment);

      const agentsInConversation = agents
        .filter(agent => conv.agentIds.includes(agent.id))
        .map(agent => agent.name);
      const inAgent = filters.agent.length === 0 || filters.agent.some(a => agentsInConversation.includes(a));
      
      const inEmotions = filters.emotions.length === 0 || filters.emotions.some(e => conv.emotions.includes(e));
      const inTopics = filters.topics.length === 0 || filters.topics.some(t => conv.topics.includes(t));

      return inSearch && inDateRange && inStatus && inSentiment && inAgent && inEmotions && inTopics;
    });
  }, [conversations, filters, agents, clients]);

  const handleSummaryFilterChange = (filterType: FilterType, date: Date | null) => {
    setSummaryFilter({ type: filterType, date: date });
  };

  const handleConversationClick = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setIsDetailOpen(true);
  };

  const handleAgentPerformanceDateChange = useCallback((newRange: DateRange) => {
    console.log('Agent performance date range has been updated to:', newRange);
    setAgentPerformanceDateRange(newRange);
  }, []);

  const handleClientPerformanceDateChange = useCallback((newRange: DateRange) => {
      console.log('Client performance date range has been updated to:', newRange);
      setClientPerformanceDateRange(newRange);
  }, []);

  if (loading) {
    return <div className="p-6 text-gray-600 text-center">⏳ Loading {currentTeam} Dashboard...</div>;
  }
  return (
    <div className="min-h-screen bg-gray-50/30">
      {/* {apiDown && (
        <div className="mx-6 mt-4 mb-2 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-red-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-.01-6a9 9 0 100 18 9 9 0 000-18z" />
          </svg>
          <span>Some data couldn’t be fetched — the API might be down. Showing cached or partial data.</span>
        </div>
      )} */}

      <div className="w-full px-6 py-6 space-y-8">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatsCard
            title="Total Conversations"
            value={stats.total}
            icon={<MessageCircle className="w-6 h-6 text-white" />}
            colorTheme="blue"
          />
          <StatsCard
            title="In Progress"
            value={stats.inProgress}
            icon={<AlertTriangle className="w-6 h-6 text-white" />}
            colorTheme="orange"
          />
          <StatsCard
            title="Solved"
            value={stats.solved}
            icon={<CheckCircle className="w-6 h-6 text-white" />}
            colorTheme="green"
          />
          <StatsCard
            title="Negative"
            value={stats.negative}
            icon={<ThumbsDown className="w-6 h-6 text-white" />}
            colorTheme="red"
          />
          <StatsCard
            title="Urgent"
            value={stats.urgent}
            icon={<AlertCircle className="w-6 h-6 text-white" />}
            colorTheme="purple"
          />
          <StatsCard
            title="Active Agents"
            value={stats.agentCount}
            icon={<MessageCircle className="w-6 h-6 text-white" />}
            colorTheme="indigo"
          />
        </div>

        {/* Current Business Summary and Trending Topics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <CurrentOperationsSummary 
            conversations={conversations} 
            stats={stats}
            apiSummary={summaryApiData}
            isLoading={isSummaryLoading}
            error={summaryError}
            currentFilterType={summaryFilter.type}
            onFilterChange={handleSummaryFilterChange}
            dateRange={summaryDateRange}
          />
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <TrendingTopics conversations={filteredConversations} />
          </div>
        </div>

        {/* Agent Performance Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <AgentPerformanceSummariser
            agentPerformance={agentPerformance}
            onDateRangeChange={handleAgentPerformanceDateChange}
          />
        </div>

        {/* Client Summary Table */}
        <ClientSummary
          clientData={clientPerformanceData}
          onDateRangeChange={handleClientPerformanceDateChange}
        />

        {/* Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <DashboardFilters filters={filters} onFiltersChange={setFilters} agents={agents} />
        </div>

        {/* Conversation Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <ConversationTable conversations={filteredConversations} onConversationClick={handleConversationClick} clients={clients} agents={agents} />
        </div>

        {/* Analytics Charts */}
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <SentimentDistributionPlot
              conversations={filteredConversations}
              onConversationClick={handleConversationClick}
              selectedRange={selectedRange}
              customDateRange={customDateRange}
              onRangeChange={setSelectedRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <EmotionDistributionOverTime
              conversations={filteredConversations}
              selectedRange={selectedRange}
              customDateRange={customDateRange}
              onRangeChange={setSelectedRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
          </div>
        </div>

        {/* Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <SentimentTrendOverTime
              conversations={filteredConversations}
              selectedRange={trendSelectedRange}
              customDateRange={trendCustomDateRange}
              onRangeChange={setTrendSelectedRange}
              onCustomDateRangeChange={setTrendCustomDateRange}
            />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <EmotionTrendOverTime
              conversations={filteredConversations}
              selectedRange={trendSelectedRange}
              customDateRange={trendCustomDateRange}
              onRangeChange={setTrendSelectedRange}
              onCustomDateRangeChange={setTrendCustomDateRange}
            />
          </div>
        </div>

        {/* Conversation Detail Sheet */}
        <ConversationDetailSheet
          conversation={selectedConversation}
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          clients={clients}
          agents={agents}
        />

        {/* AI Chat Assistant */}
        <AIChatAssistant />
      </div>
    </div>
  );
}